<template>
  <div class="matrix-board-wrapper">
    <div class="matrix-board-container">
      <!-- Fixierte erste Spalte -->
      <div v-for="(country, countryIndex) in getColumns" :key="country.id">
        <div v-if="countryIndex === 0" class="country-column poi-xy kpi-column-fixed">
          <div class="poi-xy"></div>
          <div v-for="section in sections" :key="section.id" class="kpi-section">
            <div class="section-header poi-x"
                 @click="toggleSection(section.id)"
                 :class="{ 'is-hovered-row': hoveredRow === section.id }">
              <h3>{{ section.name }}</h3>
              <ChevronDown :size="16" class="toggle-icon" :class="{ 'is-open': section.isOpen }" />
            </div>

            <div v-if="section.isOpen" class="section-content">
              <div v-for="kpi in section.kpis" :key="kpi.id"
                   class="kpi-row poi-x2"
                   :class="{ 'is-hovered-row': hoveredRow === section.id + '-' + kpi.id }">
                <div class="kpi-name">
                  <p class="kpi-title">{{ kpi.name }}</p>
                  <p class="kpi-description">{{ kpi.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scrollbare Spalten 2 bis letzte -->
      <div class="data-columns-wrapper scroll-y scroll-on-hover">
        <div class="data-columns-scroll">
          <div v-for="(country, countryIndex) in getColumns" :key="'data-' + country.id">
            <div v-if="countryIndex > 0" class="country-column poi-xy">
              <MatrixBoardItem :kpi="country"
                               :isHeader="true"
                               class="poi-xy"
                               :class="{ 'is-hovered-col': hoveredCol === country.id }" />
              <div v-for="section in sections" :key="section.id">
                <MatrixBoardItem class="poi-x"
                                 :kpi="getKpiData(country.id, section.id, section.kpis[0].id)"
                                 @mouseenter="setHover(country.id, section.id)"
                                 @mouseleave="clearHover()"
                                 :class="{ 'is-hovered-cell': hoveredCol === country.id && hoveredRow === section.id }" />
                <div v-if="section.isOpen" class="section-data">
                  <div v-for="kpi in section.kpis" :key="kpi.id">
                    <MatrixDetail :data="getDetailData(country.id, section.id, kpi.id)"
                                  class="poi-x2"
                                  @mouseenter="setHover(country.id, section.id + '-' + kpi.id)"
                                  @mouseleave="clearHover()"
                                  :class="{ 'is-hovered-cell': hoveredCol === country.id && hoveredRow === section.id + '-' + kpi.id }" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MatrixBoardItem from './MatrixBoardItem.vue';
import MatrixDetail from './MatrixDetail.vue';
import { ChevronDown } from 'lucide-vue-next';

export default {
  name: 'MatrixBoard',
  props: {
    kpiData: {
      type: Object,
      required: false
    }
  },
  components: {
    MatrixBoardItem,
    MatrixDetail,
    ChevronDown
  },

  computed: {
    getColumns() {
      if (this.countries[0].quarters.length === 0) {
        this.countries.forEach(x => { x.quarters = this.getKpiData(x.id, null, null).quarters});
      }
      return [{id: 'none',}, ...this.countries];
    }
  },

  data() {
    return {
      hoveredRow: null,
      hoveredCol: null,
      sections: [
        {
          id: 'product-presentation',
          name: 'Product Presentation',
          isOpen: true,
          kpis: [
            {
              id: 'feature-explanation',
              name: 'Feature Explanation',
              description: 'Did the salesperson explain min. 2 features?'
            },
            {
              id: 'visual-demonstration',
              name: 'Visual Demonstration',
              description: 'Was the car shown visually (Tablet/Screen)?'
            }
          ]
        },
        {
          id: 'closing-phase',
          name: 'Closing Phase',
          isOpen: false,
          kpis: [
            {
              id: 'feature-explanation',
              name: 'Feature Explanation',
              description: 'Did the salesperson explain min. 2 features?'
            },
            {
              id: 'visual-demonstration',
              name: 'Visual Demonstration',
              description: 'Was the car shown visually (Tablet/Screen)?'
            }
          ]
        }
      ],
      countries: [
        {
          id: 'DE',
          country: 'Germany',
          quarters: []
        },
        {
          id: 'FR',
          country: 'France',
          quarters: []
        },
        {
          id: 'ES',
          country: 'Spain',
          quarters: []
        },
        {
          id: 'IT',
          country: 'Italy',
          quarters: []
        },
        {
          id: 'AT',
          country: 'Austria',
          quarters: []
        },
        {
          id: 'GR',
          country: 'Greece',
          quarters: []
        },
        {
          id: 'PT',
          country: 'Portugal',
          quarters: []
        },
        {
          id: 'NL',
          country: 'Netherlands',
          quarters: []
        }
      ],
    };
  },

  beforeUnmount() {
    this.clearHover();
  },

  methods: {
    toggleSection(sectionId) {
      const section = this.sections.find(s => s.id === sectionId);
      if (section) {
        section.isOpen = !section.isOpen;
      }
    },
    getKpiData(countryId, sectionId, kpiId) {
      // Beispiel-Daten für ein KPI
      return {
        quarters: [
          {
            id: '24t4',
            label: '24T4',
            value: 92,
            progress: 92,
            change: 0,
            isHighlighted: false
          },
          {
            id: '25t1',
            label: '25T1',
            value: 97,
            progress: 97,
            change: 5,
            isHighlighted: false
          },
          {
            id: '25t2',
            label: '25T2',
            value: 94,
            progress: 94,
            change: -3,
            isHighlighted: false
          },
          {
            id: '25t3',
            label: '25T3',
            value: 97,
            progress: 97,
            change: 3,
            isHighlighted: true
          }
        ]
      };
    },
    getDetailData(countryId, sectionId, kpiId) {
      return {
        yesDetailed: 80,
        briefly: 15,
        no: 5
      };
    },
    setHover(colId, rowId) {
      this.hoveredCol = colId;
      this.hoveredRow = rowId;
    },
    clearHover() {
      this.hoveredCol = null;
      this.hoveredRow = null;
    }
  }
};
</script>

<style scoped>
.matrix-board-wrapper {
  width: 100%;
  overflow: hidden;
}

.matrix-board-container {
  display: flex;
  gap: 0;
  position: relative;
}

/* Fixierte linke Spalte */
.kpi-column-fixed {
  min-width: 16rem;
  max-width: 20rem;
  flex-shrink: 0;
  position: sticky;
  left: 0;
  z-index: 10;
  background: inherit;
  padding-right: 1rem;
}

.poi-x, .poi-xy, .poi-x2 {
  min-width: 16rem;
}

.poi-y, .poi-xy {
  min-height: 10rem;
}
.poi-x {
  min-height: 8rem;
}
.poi-x2 {
  min-height: 6rem;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  cursor: pointer;
  transition: all 0.2s;
  height: 125px;
  align-items: center;
}

.section-header:hover {
  border-radius: 4px;
  opacity: 0.9;
}

.section-header h3 {
  font-size: 1rem;
  margin: 0;
  font-weight: 500;
}

.toggle-icon {
  transition: transform 0.3s;
  font-size: 0.875rem;
}

.toggle-icon.is-open {
  transform: rotate(180deg);
}

.section-content {
  display: flex;
  flex-direction: column;
  /* gap: 1.5rem;
  padding: 0 0.75rem; */
}

.kpi-row {
  /* min-height: 157px; */
  /* height: 157px; */
  display: flex;
  flex-direction: column;
  justify-content: center;
    padding: 0.0rem 2rem;
}

.kpi-name {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.kpi-title {
  font-size: 0.875rem;
  margin: 0;
  font-weight: 500;
}

.kpi-description {
  font-size: 0.8125rem;
  margin: 0;
  opacity: 0.7;
}

.section-divider {
  height: 2px;
  background: rgba(255, 255, 255, 0.05);
  margin: 1.5rem 0;
}

/* Scrollbare Daten-Spalten */
.data-columns-wrapper {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
}

.data-columns-scroll {
  display: flex;
  gap: 1.0rem;
  min-width: max-content;
}

.country-column {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 260px;
  max-width: 260px;
  flex-shrink: 0;
}

.section-data {
  display: flex;
  flex-direction: column;
  padding: 0;
}
/* Hover Highlighting - Exakt 3 Zellen */
.is-hovered-cell,
.is-hovered-row,
.is-hovered-col {
background-color: color-mix(in srgb, var(--target-secondary) 50%, transparent) !important;
  transition: background-color 0.2s ease;
}

/* Ensure items in country column align with kpi rows */

</style>
